import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  VStack,
  Text,
  Alert,
  AlertIcon,
  Checkbox,
} from '@chakra-ui/react'
import { Eye, EyeOff, Lock, User } from 'lucide-react'
import { loginSchema, type LoginFormData } from '@/lib/validations/auth'
import { useAuthStore } from '@/store/auth'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useSiteSettings } from '@/hooks/useSiteSettings'

export function LoginForm() {
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')
  const navigate = useNavigate()
  const { login, isLoading } = useAuthStore()
  const { settings: siteSettings } = useSiteSettings()
  const colors = useThemeColors()

  const siteName = import.meta.env.VITE_APP_NAME || 'Sign In'
  const siteDescription =
    import.meta.env.VITE_APP_DESCRIPTION || 'Enter your credentials to access your account'

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  })

  // Prefill client from query param if present and show setup messages
  const [searchParams] = useSearchParams()
  React.useEffect(() => {
    const client = searchParams.get('client')
    const setup = searchParams.get('setup')
    const already = searchParams.get('already_activated')

    if (client) {
      setValue('user_login', client)
    }

    if (setup === 'success') {
      setInfo('Your password has been set. Please sign in with your credentials.')
    } else if (already === '1') {
      setInfo('Your account has already been set up. Please sign in.')
    }
  }, [searchParams, setValue])

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError('')
      console.log('[LoginForm] Starting login for:', data.user_login)

      const loginData = {
        ...data,
        remember_me: rememberMe,
      }

      // login expects LoginCredentials (email field present) but we use user_login (client id)
      // Cast to any to preserve current behavior where backend accepts either email or user_login
      const result = await login(loginData as any)

      if (result.success) {
        navigate('/dashboard')
      } else if (result.twoFactorSetupRequired) {
        window.location.href = `/two-factor/setup?user_login=${encodeURIComponent(
          data.user_login
        )}&remember_me=${rememberMe ? '1' : '0'}`
      } else if (result.twoFactorRequired) {
        window.location.href = `/two-factor?user_login=${encodeURIComponent(
          data.user_login
        )}&remember_me=${rememberMe ? '1' : '0'}`
      } else {
        setError(result.message || 'Login failed')
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        const minutes = err.response?.data?.lockout_remaining_minutes
        setError(
          minutes
            ? `Too many login attempts. Try again in ${minutes} minute(s).`
            : 'Too many login attempts. Please try again later.'
        )
      } else {
        setError(err.response?.data?.message || 'Login failed')
      }
    }
  }

  return (
    <Box minH="100vh" bg={colors.bg} display="flex" alignItems="center" justifyContent="center">
      <Card maxW="md" w="full" bg={colors.cardBg}>
        <CardBody p={6}>
          <VStack spacing={4}>
            <Box textAlign="center">
              <Box mb={2} display="flex" justifyContent="center">
                <img
                  src={siteSettings.auth_logo || '/assets/img/als-logo-2.png'}
                  alt={siteName}
                  style={{ maxWidth: '300px' }}
                />
              </Box>

              <Heading size="lg" color={colors.heading} mb={1}>
                {siteName}
              </Heading>
              <Text color={colors.text} fontSize="sm">
                {siteDescription}
              </Text>
            </Box>

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {info && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                {info}
              </Alert>
            )}

            <Box as="form" onSubmit={handleSubmit(onSubmit)} w="100%">
              <VStack spacing={3}>
                {/* Client ID / User Login */}
                <FormControl isInvalid={!!errors.user_login}>
                  <FormLabel>Client ID</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <User size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('user_login')}
                      type="text"
                      placeholder="Enter your Client ID"
                      pl="10"
                    />
                  </InputGroup>
                  <FormErrorMessage>{errors.user_login?.message}</FormErrorMessage>
                </FormControl>

                {/* Password */}
                <FormControl isInvalid={!!errors.password}>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <Lock size={20} />
                    </InputLeftElement>
                    <Input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter your password"
                      pl="10"
                    />
                    <InputRightElement width="3rem">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        type="button"
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </Button>
                    </InputRightElement>
                  </InputGroup>
                  <FormErrorMessage>{errors.password?.message}</FormErrorMessage>
                </FormControl>

                {/* Remember + Forgot */}
                <Box w="100%" display="grid" gridTemplateColumns="1fr 1fr">
                  <Checkbox
                    isChecked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    size="sm"
                  >
                    Remember me
                  </Checkbox>

                  <Button
                    variant="link"
                    fontSize="sm"
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot your password?
                  </Button>
                </Box>

                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  w="full"
                  isLoading={isLoading}
                >
                  Sign In
                </Button>
              </VStack>
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  )
}
